import type { AxiosError, AxiosInstance } from 'axios';
import { openKillSwitchGateImperative } from '@/store/killSwitchGateStore';

interface KillSwitchBody {
  code?: string;
  switch?: string;
}

/**
 * Backstop for the server-side kill switches. A `503 { code: 'MAINTENANCE' }`
 * (site-wide) surfaces the kill-switch gate. This is the reactive complement
 * to the client-side LD UX — it covers the window where the client's flag view
 * is stale. The original 503 always propagates so callers still see the error.
 *
 * `FEATURE_DISABLED` (one capability) deliberately does NOT open the gate:
 * the popup was deemed too disruptive for a single paused action. The
 * propagated 503 surfaces through the caller's inline error handling, and the
 * client-side LD UX (disabled CTAs) covers the steady state.
 *
 * Returns a teardown function (tests / HMR).
 */
export function installKillSwitchInterceptor(instance: AxiosInstance): () => void {
  const id = instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const body = error.response?.data as KillSwitchBody | undefined;
      if (error.response?.status === 503 && body?.code === 'MAINTENANCE') {
        openKillSwitchGateImperative({
          kind: 'MAINTENANCE',
          switchKey: body.switch ?? null,
        });
      }
      throw error;
    },
  );
  return () => instance.interceptors.response.eject(id);
}
